import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import Quasar from 'quasar/src/vue-plugin.js';;
import quasarUserOptions from "./quasar-user-options";
import axios from "axios";
import CKEditor from "@ckeditor/ckeditor5-vue";
import CountryFlag from "vue-country-flag-next";
import VueFroala from "vue-froala-wysiwyg";
//Import Froala Editor
import "froala-editor/js/plugins.pkgd.min.js";
//Import third party plugins
import "froala-editor/js/third_party/embedly.min";
import "froala-editor/js/third_party/font_awesome.min";
import "froala-editor/js/third_party/image_tui.min";
// Import Froala Editor css files.
import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/css/froala_style.min.css";
import authService from "./services/auth.service";
import requestState from "./helpers/requestState";
import store from "./store";
import Cookies from "js-cookie";
import "devextreme/dist/css/dx.material.blue.light.compact.css";
import cfg from "devextreme/core/config";
import { licenseKey } from "./devextreme-license";

cfg({ licenseKey });

const app = createApp(App)
  .use(Quasar, quasarUserOptions)
  .use(router)
  .use(CKEditor)
  .use(VueFroala);

axios.interceptors.request.use(function(config) {
  config.headers["Accept-Language"] = "en-US";
  let xsrfCookieKey = localStorage.getItem("xsrf-cookie-key");
  if (xsrfCookieKey) {
    const xsrfToken = Cookies.get(xsrfCookieKey);
    if (xsrfToken && ["post", "put", "delete"].includes(config.method)) {
      config.headers["X-XSRF-TOKEN"] = xsrfToken;
    }
  }
  return config;
});

axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // ignore refreshing token if the first try fails
    if (originalRequest.url.includes("/auth/refresh")) {
      if (!requestState.isLogoutInProgress) {
        requestState.isLogoutInProgress = true;
        const returnUrl = originalRequest.url.includes("/api")
          ? ""
          : originalRequest.url;
        router.push({
          name: "Login",
          query: { returnUrl }
        });
      }
      return Promise.reject(error);
    }

    // if authentication fails, try to get new access token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (requestState.isLogoutInProgress) {
        return Promise.reject(error);
      }

      try {
        if (requestState.isRateLimitActive) {
          return Promise.reject(error);
        }

        if (!requestState.isRefreshing) {
          requestState.isRefreshing = true;
          requestState.refreshPromise = authService
            .refreshToken()
            .then(() => authService.getAuthUserData())
            .finally(() => {
              requestState.isRefreshing = false;
            });
        }
        // wait for the refreshPromise
        await requestState.refreshPromise;
        // Retry the originalRequest
        return axios(originalRequest);
      } catch (refreshTokenError) {
        // if server returns 429 (too many requests), pause all requests for 30 seconds
        if (refreshTokenError.response?.status === 429) {
          requestState.isRateLimitActive = true;

          store.commit("auth/enableModalError");
          store.commit(
            "auth/updateModalError",
            refreshTokenError.response.data
          );

          await authService.logout(false);
          requestState.isRefreshing = false;
          requestState.isLogoutInProgress = false;
          requestState.refreshPromise = null;

          await router.push({
            name: "Login"
          });

          setTimeout(() => {
            requestState.isRateLimitActive = false;
          }, 60000);
          return Promise.reject(refreshTokenError);
        } else if (refreshTokenError.response?.status === 403) {
          // Refresh token is not valid, clear authUser and don't call logout on the server side
          await authService.logout(false);
          requestState.isRefreshing = false;
          requestState.isLogoutInProgress = false;
          requestState.refreshPromise = null;
          const returnUrl = originalRequest.url.includes("/api")
            ? ""
            : originalRequest.url;
          router.push({
            name: "Login",
            query: { returnUrl }
          });

          return Promise.reject(refreshTokenError);
        } else {
          await authService.logout(false);
          requestState.isRefreshing = false;
          requestState.isLogoutInProgress = false;
          requestState.refreshPromise = null;
          return Promise.reject(refreshTokenError);
        }
      }
    }

    // if authorization fails, log out user
    if (error.response?.status === 403) {
      requestState.isRefreshing = false;
      requestState.isLogoutInProgress = false;
      requestState.refreshPromise = null;
      authService.logout(true);
      router.push({
        name: "Login"
      });
    }
    return Promise.reject(error);
  }
);

router.isReady().then(() => {
  app.mount("#app");
});

app.component("country-flag", CountryFlag);
